import type { PropsWithChildren, ReactElement } from "react";
import { useState } from "react";

import useActionAfterFlagChange from "app/pages/sales-intelligence/hooks/useActionAfterFlagChange";
import SuggestionsIndicator from "app/pages/sales-intelligence/sub-modules/column-suggestions/components/SuggestionsIndicator/SuggestionsIndicator";

import ColumnSuggestionsTooltipContentTracked from "../../containers/ColumnSuggestionsTooltipContent/ColumnSuggestionsTooltipContentTracked";
import useColumnSuggestionsWithLifecycle from "../../hooks/useColumnSuggestionsWithLifecycle";
import type ColumnSuggestion from "../../models/ColumnSuggestion";
import { columnSuggestionsLocalStorage } from "../../services/storage/columnSuggestionsStorage";
import ColumnSuggestionsTooltipBase from "../ColumnSuggestionsTooltip/ColumnSuggestionsTooltipBase";
import SuggestionsNotificationTooltip from "../SuggestionsNotificationTooltip/SuggestionsNotificationTooltip";

type SuggestionsViewTriggerProps = {
    isViewOpen: boolean;
    onViewToggle(): void;
};

type Props = {
    isTriggerDisabled: boolean;
    columnSuggestions: readonly ColumnSuggestion[];
    renderTrigger(props: Readonly<SuggestionsViewTriggerProps>): ReactElement;
    onApplyClick(columnNames: readonly string[]): void;
    onCancelClick(): void;
};

export const ColumnSuggestionsView = (props: PropsWithChildren<Props>) => {
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);

    const { uiColumnSuggestions, updateSeenSuggestions, removeSeenSuggestions } = useColumnSuggestionsWithLifecycle(
        props.columnSuggestions,
    );

    const isViewOpen = isTooltipOpen || isNotificationOpen;
    const arePendingSuggestionsPresent = uiColumnSuggestions.some(({ isNew }) => isNew);
    const isNotificationSeen = columnSuggestionsLocalStorage.getIsNotificationSeen();

    useActionAfterFlagChange(isTooltipOpen, () => updateSeenSuggestions(props.columnSuggestions), !isTooltipOpen);

    useActionAfterFlagChange(
        isNotificationOpen,
        () => columnSuggestionsLocalStorage.setIsNotificationSeen(true),
        !isNotificationOpen,
    );

    const handleTooltipClose = (): void => {
        setIsTooltipOpen(false);
    };

    const handleNotificationOpen = (): void => {
        if (isNotificationSeen) {
            return;
        }

        setIsNotificationOpen(true);
    };

    const handleNotificationClose = (): void => {
        setIsNotificationOpen(false);
    };

    const handleSuggestionsApply = (columnNames: readonly string[]): void => {
        handleTooltipClose();

        removeSeenSuggestions(columnNames);
        props.onApplyClick(columnNames);
    };

    const handleCancelClick = (): void => {
        handleTooltipClose();

        props.onCancelClick();
    };

    const handleViewToggle = () => {
        if (isNotificationOpen) {
            handleNotificationClose();
        }

        setIsTooltipOpen((currentIsOpen) => !currentIsOpen);
    };

    return (
        <SuggestionsIndicator
            isVisible={!props.isTriggerDisabled}
            isPending={arePendingSuggestionsPresent}
            withAnimation={!isTooltipOpen}
            onAnimationStart={handleNotificationOpen}
        >
            <SuggestionsNotificationTooltip
                isEnabled={!isNotificationSeen}
                isOpen={isNotificationOpen}
                onClose={handleNotificationClose}
            >
                <ColumnSuggestionsTooltipBase
                    isOpen={isTooltipOpen}
                    content={() => (
                        <ColumnSuggestionsTooltipContentTracked
                            columnsSuggestions={uiColumnSuggestions}
                            onApplyClick={handleSuggestionsApply}
                            onCancelClick={handleCancelClick}
                        />
                    )}
                    onClose={handleTooltipClose}
                >
                    {props.renderTrigger({
                        isViewOpen,
                        onViewToggle: handleViewToggle,
                    })}
                </ColumnSuggestionsTooltipBase>
            </SuggestionsNotificationTooltip>
        </SuggestionsIndicator>
    );
};
